import { Lightbulb, BookOpen, Flame } from "lucide-react";

export const LandingBenefits = () => {
  const benefits = [
    {
      icon: Lightbulb,
      title: "Entenda a si mesmo",
      description:
        "Converse com a KopfAI após um dia estressante e desenvolva habilidades práticas para lidar com esses desafios emocionais, reduzir ansiedade e fortalecer sua saúde mental.",
    },
    {
      icon: BookOpen,
      title: "Seu espaço seguro para desabafar",
      description:
        "Use a KopfAI como seu diário particular, um lugar onde você pode falar o que quiser, sem julgamentos, a qualquer hora e em qualquer lugar.",
    },
    {
      icon: Flame,
      title: "Precisa de calma? Agora.",
      description:
        "Se a ansiedade bater forte, peça ajuda imediata. A KopfAI oferece técnicas de respiração, meditação e acalento quando você mais precisa.",
    },
  ];

  return (
    <section className="relative w-full min-h-[710px] py-16 md:py-24 px-6 md:px-12 overflow-hidden">
      {/* Aqueous Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#D8DEE9]/20 via-white to-[#D8DEE9]/30 -z-10"></div>

      {/* Floating Blobs */}
      <div
        className="absolute top-0 left-[20%] w-[600px] h-[600px] rounded-full opacity-10 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(74, 78, 105, 0.4) 0%, rgba(74, 78, 105, 0) 70%)",
          animation: "drift 25s ease-in-out infinite",
        }}
      ></div>
      <div
        className="absolute bottom-0 right-[15%] w-[500px] h-[500px] rounded-full opacity-15 blur-3xl -z-10"
        style={{
          background: "radial-gradient(circle, rgba(255, 209, 102, 0.5) 0%, rgba(255, 209, 102, 0) 70%)",
          animation: "drift 30s ease-in-out infinite reverse",
        }}
      ></div>

      <div
        className="text-center mb-12"
        style={{
          animation: "fadeInUp 0.8s ease-out",
        }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-[#4A4E69] mb-4">Como a KopfAI ajuda você</h2>
        <p className="text-base md:text-lg text-[#272D37]/70 max-w-3xl mx-auto">
          Do alívio imediato à reestruturação profunda de pensamentos, a KopfAI guia você.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-8 justify-center items-stretch max-w-6xl mx-auto">
        {benefits.map((benefit, index) => (
          <div
            key={index}
            className="group w-full md:w-[341px] min-h-[342px] rounded-3xl bg-white/60 backdrop-blur-xl border border-[#D8DEE9]/40 shadow-lg p-8 flex flex-col transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-[#FFD166]/50 relative overflow-hidden"
            style={{
              animation: `fadeInUp 0.8s ease-out ${0.2 + index * 0.15}s backwards`,
              boxShadow: "0 10px 40px rgba(74, 78, 105, 0.08)",
            }}
          >
            {/* Hover glow effect */}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-2xl"
              style={{
                background:
                  index === 0
                    ? "radial-gradient(circle at 30% 30%, rgba(255, 209, 102, 0.3), transparent 70%)"
                    : index === 1
                      ? "radial-gradient(circle at 30% 30%, rgba(74, 78, 105, 0.2), transparent 70%)"
                      : "radial-gradient(circle at 30% 30%, rgba(255, 209, 102, 0.25), transparent 70%)",
              }}
            ></div>

            {/* Icon Container */}
            <div
              className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFD166] to-[#FFD166]/80 flex items-center justify-center mb-4 shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:rotate-3"
              style={{
                boxShadow: "0 8px 20px rgba(255, 209, 102, 0.3)",
              }}
            >
              <benefit.icon className="w-8 h-8 text-[#4A4E69] transition-transform duration-500 group-hover:scale-110" />
            </div>

            <h3 className="text-xl font-bold text-[#4A4E69] mb-4 transition-colors duration-300 group-hover:text-[#272D37]">
              {benefit.title}
            </h3>

            <p className="text-base text-[#272D37]/75 leading-relaxed">{benefit.description}</p>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#FFD166] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes drift {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
          }
          50% { 
            transform: translate(50px, 30px) scale(1.15); 
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};
